import React from 'react';
import PropTypes from 'prop-types';
import { Box, MultiSelect, Text, Tooltip } from '@mantine/core';

import { SKIP_THE_DISHES } from 'constants/integrations';
import {
  ALCOHOLIC,
  ALCOHOLIC_AVAILABLE_ALONE,
  ALCOHOLIC_NOT_AVAILABLE_ALONE,
  MENU_MANAGEMENT_TOOLTIP_WIDTH,
  buildAvailableClassifications,
  buildSkipClassifications,
} from 'constants/menuManagement';

import { useMenuManagement } from 'hooks/useMenuManagement';
import { useTranslation } from 'i18n';

const NS = 'menuManagementClassificationSelector';

const CLASSIFICATION_TOOLTIP_TEXT =
  'Specific classifications will only show on the menu when supported by the online ordering platform.';

export default function ClassificationSelector({
  selectedOptions,
  setSelectedOptions,
  recordUuid,
  title,
  error,
}) {
  const { t } = useTranslation(NS);
  const { singleMenu, checkParentMenuHasIntegration, getAvailableClassificationsFromState, getClassificationsLoadingState } =
    useMenuManagement();
  const isLoading = getClassificationsLoadingState();
  const hasSkipIntegration = checkParentMenuHasIntegration(
    SKIP_THE_DISHES.toLowerCase(),
    singleMenu,
  );

  const backendClassifications = getAvailableClassificationsFromState();
  const baseClassifications = buildAvailableClassifications(backendClassifications);
  const skipClassifications = buildSkipClassifications(backendClassifications);

  const hasOldSkipTax =
    !hasSkipIntegration &&
    selectedOptions
      .map(option => option.classification)
      .filter(
        name =>
          // alcoholic is always there if either of the alcoholic options are selected
          // does not count as an old skip tax
          name !== ALCOHOLIC && !Object.keys(baseClassifications).includes(name),
      )[0];

  let classificationsToUse = baseClassifications;
  if (hasSkipIntegration) {
    classificationsToUse = skipClassifications;
  } else if (hasOldSkipTax) {
    classificationsToUse = {
      ...baseClassifications,
      ...{ [hasOldSkipTax]: skipClassifications[hasOldSkipTax] },
    };
  }

  const handleChange = values => {
    const updatedSelectedOptions = [...selectedOptions];

    selectedOptions.forEach(option => {
      const getClassificationValue = item => {
        if (item.itemClass === ALCOHOLIC) {
          return item.availableOrderAlone
            ? ALCOHOLIC_AVAILABLE_ALONE
            : ALCOHOLIC_NOT_AVAILABLE_ALONE;
        }
        return item.itemClass;
      };

      const stillSelected = values.includes(getClassificationValue(option));
      if (!stillSelected && !option.isDeleted) {
        const index = updatedSelectedOptions.findIndex(
          o =>
            o.itemClass === option.itemClass &&
            o.availableOrderAlone === option.availableOrderAlone,
        );
        if (index !== -1) {
          updatedSelectedOptions[index] = { ...option, isDeleted: true };
        }
      }
    });

    values.forEach(optionValue => {
      const matchingClassification = classificationsToUse[optionValue];
      const matchingOptionIndex = updatedSelectedOptions.findIndex(
        selected =>
          selected.itemClass === matchingClassification.classification &&
          selected.availableOrderAlone === matchingClassification.alone,
      );

      if (matchingOptionIndex !== -1) {
        updatedSelectedOptions[matchingOptionIndex] = {
          ...updatedSelectedOptions[matchingOptionIndex],
          availableOrderAlone: matchingClassification.alone,
          itemClass: matchingClassification.classification,
          isDeleted: false,
        };
      } else {
        updatedSelectedOptions.push({
          availableOrderAlone: matchingClassification.alone,
          itemClass: matchingClassification.classification,
          isDeleted: false,
          ...(recordUuid && { itemUuid: recordUuid }),
        });
      }
    });

    setSelectedOptions(updatedSelectedOptions);
  };

  const formatValues = () =>
    selectedOptions
      .filter(selected => !selected.isDeleted)
      .map(selected => {
        if (selected.itemClass === ALCOHOLIC) {
          return selected.availableOrderAlone
            ? ALCOHOLIC_AVAILABLE_ALONE
            : ALCOHOLIC_NOT_AVAILABLE_ALONE;
        }
        return selected?.itemClass;
      });

  const data = Object.keys(classificationsToUse)
    .map(classification => ({
      label: classificationsToUse[classification]?.label,
      value: classification,
    }))
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));

  if (data.length === 0 && !isLoading) {
    return <Text c="dimmed" size="sm">No classification options found</Text>;
  }

  return (
    <Tooltip
      label={CLASSIFICATION_TOOLTIP_TEXT}
      w={MENU_MANAGEMENT_TOOLTIP_WIDTH}
      multiline
    >
      <Box>
        <MultiSelect
          label={title}
          description={t('ClassificationSelector.alcoholicDescription')}
          data={data}
          value={formatValues()}
          onChange={handleChange}
          placeholder={formatValues().length === 0 ? 'Select an option' : null}
          searchable
          error={error}
        />
      </Box>
    </Tooltip>
  );
}

ClassificationSelector.propTypes = {
  setSelectedOptions: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  selectedOptions: PropTypes.array.isRequired,
  recordUuid: PropTypes.string,
  title: PropTypes.string,
  error: PropTypes.string,
};
